import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { Inbox, Phone, MapPin, Clock, MessageSquare, CheckCircle2, AlertCircle, Loader2, MessageCircle, Mail, ListFilter, Sparkles, Search, UserPlus, StickyNote, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  neu: { label: "Neu", color: "bg-amber-100 text-amber-800", icon: AlertCircle },
  bearbeitung: { label: "In Bearbeitung", color: "bg-cyan-100 text-cyan-800", icon: Clock },
  erledigt: { label: "Erledigt", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
};

function NotizFeld({ anfrage, onSave }) {
  const [val, setVal] = useState(anfrage.notizen || "");
  const [open, setOpen] = useState(Boolean(anfrage.notizen));
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="mt-2 text-xs text-slate-500 hover:text-cyan-700 inline-flex items-center gap-1" data-testid={`notiz-oeffnen-${anfrage.id}`}><StickyNote className="h-3.5 w-3.5" />Interne Notiz hinzufügen</button>;
  return (
    <div className="mt-3">
      <textarea value={val} onChange={e => setVal(e.target.value)} rows={2} placeholder="Interne Notiz (z. B. Rückruf vereinbart)…" className="w-full text-sm rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 focus:outline-none focus:ring-2 focus:ring-amber-300" data-testid={`notiz-${anfrage.id}`} />
      {val !== (anfrage.notizen || "") && <Button size="sm" variant="outline" onClick={() => onSave(anfrage.id, val)} className="mt-1.5 text-xs rounded-lg" data-testid={`notiz-speichern-${anfrage.id}`}>Notiz speichern</Button>}
    </div>
  );
}

export default function CRMAnfragen({ bereich }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("alle");
  const [typeFilter, setTypeFilter] = useState("alle");
  const [gebietFilter, setGebietFilter] = useState("alle");
  const [searchQ, setSearchQ] = useState("");
  const [gebietNames, setGebietNames] = useState([]);

  const headers = { Authorization: `Bearer ${user?.token}` };

  useEffect(() => {
    fetchAnfragen();
    axios.get(`${API}/gebiete`).then(res => setGebietNames(res.data.map(g => g.name))).catch(() => {});
  }, []);

  const fetchAnfragen = async () => {
    try {
      const res = await axios.get(`${API}/alltagsbegleitung/anfragen`, { headers });
      setAnfragen(bereich === "pflegeberatung" ? res.data.filter((a) => a.type === "kontaktformular" || a.type === "pflegeberatung") : bereich === "alltagsbegleitung" ? res.data.filter((a) => a.type !== "kontaktformular" && a.type !== "pflegeberatung") : res.data);
    } catch { toast.error("Anfragen konnten nicht geladen werden."); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/alltagsbegleitung/anfragen/${id}/status`, { status }, { headers });
      setAnfragen(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      toast.success("Status aktualisiert");
    } catch { toast.error("Fehler."); }
  };

  const saveNotiz = async (id, notizen) => {
    try {
      await axios.patch(`${API}/alltagsbegleitung/anfragen/${id}/notizen`, { notizen }, { headers });
      setAnfragen(prev => prev.map(a => a.id === id ? { ...a, notizen } : a));
      toast.success("Notiz gespeichert");
    } catch { toast.error("Notiz konnte nicht gespeichert werden."); }
  };

  const alsKunde = async (a) => {
    try {
      const res = await axios.post(`${API}/alltagsbegleitung/anfragen/${a.id}/als-kunde`, {}, { headers });
      setAnfragen(prev => prev.map(x => x.id === a.id ? { ...x, customer_id: res.data.customer_id, status: x.status === "neu" ? "bearbeitung" : x.status } : x));
      toast.success(res.data.neu ? "Kundendatensatz angelegt" : "Bestehendem Kunden zugeordnet");
      navigate(`/app/customers/${res.data.customer_id}`);
    } catch { toast.error("Kunde konnte nicht angelegt werden."); }
  };

  // Filter logic
  const filtered = anfragen.filter(a => {
    if (statusFilter !== "alle" && a.status !== statusFilter) return false;
    if (typeFilter === "warteliste" && a.kapazitaetsstatus !== "warteliste") return false;
    if (typeFilter === "fensterreinigung" && a.fensterreinigung_interesse !== true) return false;
    if (typeFilter === "kontaktformular" && a.type !== "kontaktformular") return false;
    if (typeFilter === "alltagsbegleitung" && a.type === "kontaktformular") return false;
    if (gebietFilter !== "alle" && (a.gebiet || a.stadt || "").toLowerCase() !== gebietFilter.toLowerCase()) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      if (!(a.name || "").toLowerCase().includes(q) && !(a.phone || "").includes(q) && !(a.email || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const gebiete = [...new Set(anfragen.map(a => a.gebiet || a.stadt).filter(Boolean))].sort();
  const warteCount = anfragen.filter(a => a.kapazitaetsstatus === "warteliste").length;
  const fensterCount = anfragen.filter(a => a.fensterreinigung_interesse === true).length;
  const kontaktCount = anfragen.filter(a => a.type === "kontaktformular").length;

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;

  return (
    <div data-testid="crm-anfragen">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">{bereich === "pflegeberatung" ? "Beratungsanfragen" : "Anfragen Alltagsbegleitung"}</h1>
          <p className="text-sm text-slate-500 mt-1">{anfragen.length} Anfragen – {bereich === "pflegeberatung" ? "Kontaktformular der Website" : "Anfrageformular und Warteliste der Alltagsbegleitung"}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 text-center cursor-pointer hover:shadow-md transition-all" onClick={() => { setStatusFilter("neu"); setTypeFilter("alle"); }}>
          <p className="text-xl font-bold text-amber-700">{anfragen.filter(a => a.status === "neu").length}</p>
          <p className="text-xs text-slate-500">Neu</p>
        </div>
        <div className="bg-cyan-50 rounded-xl p-3 border border-cyan-100 text-center cursor-pointer hover:shadow-md transition-all" onClick={() => { setStatusFilter("bearbeitung"); setTypeFilter("alle"); }}>
          <p className="text-xl font-bold text-cyan-700">{anfragen.filter(a => a.status === "bearbeitung").length}</p>
          <p className="text-xs text-slate-500">In Bearbeitung</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 border border-green-100 text-center cursor-pointer hover:shadow-md transition-all" onClick={() => { setStatusFilter("erledigt"); setTypeFilter("alle"); }}>
          <p className="text-xl font-bold text-green-700">{anfragen.filter(a => a.status === "erledigt").length}</p>
          <p className="text-xs text-slate-500">Erledigt</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-3 border border-purple-100 text-center cursor-pointer hover:shadow-md transition-all" onClick={() => { setTypeFilter("warteliste"); setStatusFilter("alle"); }}>
          <p className="text-xl font-bold text-purple-700">{warteCount}</p>
          <p className="text-xs text-slate-500">Warteliste</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-3 border border-orange-100 text-center cursor-pointer hover:shadow-md transition-all" onClick={() => { setTypeFilter("fensterreinigung"); setStatusFilter("alle"); }}>
          <p className="text-xl font-bold text-orange-700">{fensterCount}</p>
          <p className="text-xs text-slate-500">Fensterreinigung</p>
        </div>
        <div className="bg-sky-50 rounded-xl p-3 border border-sky-100 text-center cursor-pointer hover:shadow-md transition-all" onClick={() => { setTypeFilter("kontaktformular"); setStatusFilter("alle"); }} data-testid="stat-kontaktformular">
          <p className="text-xl font-bold text-sky-700">{kontaktCount}</p>
          <p className="text-xs text-slate-500">Kontaktformular</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <ListFilter className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="flex gap-1.5 flex-wrap">
              {["alle", "neu", "bearbeitung", "erledigt"].map(f => (
                <button key={f} onClick={() => setStatusFilter(f)} data-testid={`filter-${f}`}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${statusFilter === f ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {f === "alle" ? "Alle Status" : STATUS_CONFIG[f]?.label}
                </button>
              ))}
              <span className="text-slate-300 mx-1">|</span>
              {[{k:"alle",l:"Alle Typen"},{k:"alltagsbegleitung",l:"Alltagsbegleitung"},{k:"kontaktformular",l:"Kontaktformular"},{k:"warteliste",l:"Warteliste"},{k:"fensterreinigung",l:"Fensterreinigung"}].map(({k,l}) => (
                <button key={k} onClick={() => setTypeFilter(k)} data-testid={`filter-type-${k}`}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${typeFilter === k ? "bg-purple-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setGebietFilter("alle")} data-testid="filter-gebiet-alle" className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${gebietFilter === "alle" ? "bg-green-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                Alle Gebiete
              </button>
              {gebietNames.map(g => (
                <button key={g} onClick={() => setGebietFilter(g)} data-testid={`filter-gebiet-${g.toLowerCase()}`}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${gebietFilter === g ? "bg-green-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {g}
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input data-testid="anfragen-search" value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Suche..." className="h-8 pl-8 text-xs rounded-lg w-44" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-slate-400 mb-3">{filtered.length} Ergebnis{filtered.length !== 1 ? "se" : ""}</p>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Keine Anfragen für diese Filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(a => {
            const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.neu;
            const Icon = cfg.icon;
            const isWarte = a.kapazitaetsstatus === "warteliste";
            const hasFenster = a.fensterreinigung_interesse === true;
            const isKontakt = a.type === "kontaktformular";
            return (
              <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all" data-testid={`anfrage-${a.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="text-lg font-bold text-slate-900">{a.name}</h3>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.color}`}>
                        <Icon className="h-3 w-3" /> {cfg.label}
                      </span>
                      {isKontakt && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-800" data-testid={`badge-kontakt-${a.id}`}>
                          <Mail className="h-3 w-3" /> Kontaktformular
                        </span>
                      )}
                      {a.customer_id && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <UserPlus className="h-3 w-3" /> Kunde angelegt
                        </span>
                      )}
                      {isWarte && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-800">
                          <Clock className="h-3 w-3" /> Warteliste
                        </span>
                      )}
                      {hasFenster && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-100 text-orange-800">
                          <Sparkles className="h-3 w-3" /> Fensterreinigung
                        </span>
                      )}
                      {a.quelle === "website-warteliste" && (
                        <span className="text-xs text-slate-400">via Warteliste</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1 hover:text-cyan-700"><Phone className="h-3.5 w-3.5" /> {a.phone}</a>}
                      {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-1 hover:text-cyan-700"><Mail className="h-3.5 w-3.5" /> {a.email}</a>}
                      {(a.gebiet || a.stadt) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {a.gebiet || a.stadt}</span>}
                      {a.pflegegrad && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">PG {a.pflegegrad}</span>}
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {formatDate(a.created_at)}</span>
                    </div>
                    {a.nachricht && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="text-sm text-slate-600 flex items-start gap-2 whitespace-pre-line">
                          <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                          {a.nachricht}
                        </p>
                      </div>
                    )}
                    <NotizFeld anfrage={a} onSave={saveNotiz} />
                    {a.notifications && (
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <span className={`inline-flex items-center gap-1 ${a.notifications.whatsapp === "versendet" ? "text-green-600" : a.notifications.whatsapp === "fehlgeschlagen" ? "text-red-500" : "text-slate-400"}`}>
                          <MessageCircle className="h-3 w-3" />
                          WhatsApp: {a.notifications.whatsapp === "versendet" ? "✓" : a.notifications.whatsapp === "fehlgeschlagen" ? "⚠" : "—"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex sm:flex-col gap-2 shrink-0 flex-wrap">
                    {a.customer_id ? (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/app/customers/${a.customer_id}`)} className="text-xs rounded-lg" data-testid={`kunde-oeffnen-${a.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1" />Kunde öffnen</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => alsKunde(a)} className="text-xs rounded-lg border-cyan-300 text-cyan-800" data-testid={`als-kunde-${a.id}`}><UserPlus className="h-3.5 w-3.5 mr-1" />Als Kunde übernehmen</Button>
                    )}
                    {a.status !== "bearbeitung" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "bearbeitung")}
                        className="text-xs rounded-lg">In Bearbeitung</Button>
                    )}
                    {a.status !== "erledigt" && (
                      <Button size="sm" onClick={() => updateStatus(a.id, "erledigt")}
                        className="text-xs bg-green-600 hover:bg-green-700 text-white rounded-lg">Erledigt</Button>
                    )}
                    {a.status === "erledigt" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "neu")}
                        className="text-xs rounded-lg">Reaktivieren</Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
